import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import api, { API } from "../api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Printer, DownloadCloud } from "lucide-react";

const ReportTable = ({ rows, total, totalLabel, fmt, testid }) => (
  <table className="w-full text-sm" data-testid={testid}>
    <tbody>
      {rows.map((r) => (
        <tr key={r.code} className="border-b border-border">
          <td className="py-2 font-mono text-xs text-muted-foreground w-16">{r.code}</td>
          <td className="py-2">{r.name}</td>
          <td className="py-2 text-right font-mono">{fmt(r.amount)}</td>
        </tr>
      ))}
      <tr className="font-semibold">
        <td></td>
        <td className="py-2 uppercase text-xs tracking-wider">{totalLabel}</td>
        <td className="py-2 text-right font-mono">{fmt(total)}</td>
      </tr>
    </tbody>
  </table>
);

function ContoEconomico({ entity, fmt, t }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/reports/conto-economico?entity_id=${entity}`).then((r) => setData(r.data)).catch(() => {});
  }, [entity]);
  if (!data) return <div className="text-sm text-muted-foreground">{t("common.loading")}</div>;
  return (
    <div className="print-area grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="conto-economico-table">
      <div>
        <h3 className="font-heading font-semibold mb-3 text-emerald-500">{t("rep.revenues")}</h3>
        <ReportTable rows={data.revenues} total={data.total_revenues} totalLabel={t("rep.revenues")} fmt={fmt} testid="ce-revenues-table" />
      </div>
      <div>
        <h3 className="font-heading font-semibold mb-3 text-red-500">{t("rep.expenses")}</h3>
        <ReportTable rows={data.expenses} total={data.total_expenses} totalLabel={t("rep.expenses")} fmt={fmt} testid="ce-expenses-table" />
      </div>
      <div className="md:col-span-2 border-t-2 border-border pt-4 flex justify-between items-center">
        <span className="font-heading font-bold text-lg">{t("rep.result")}</span>
        <span className={`font-mono font-bold text-xl ${data.result >= 0 ? "text-emerald-500" : "text-red-500"}`} data-testid="ce-result">
          {fmt(data.result)}
        </span>
      </div>
    </div>
  );
}

function StatoPatrimoniale({ entity, fmt, t }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/reports/stato-patrimoniale?entity_id=${entity}`).then((r) => setData(r.data)).catch(() => {});
  }, [entity]);
  if (!data) return <div className="text-sm text-muted-foreground">{t("common.loading")}</div>;
  const eqRows = [...data.equity, { code: "", name: t("rep.result"), amount: data.period_result }];
  return (
    <div className="print-area grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="stato-patrimoniale-table">
      <div>
        <h3 className="font-heading font-semibold mb-3 text-blue-500">{t("rep.assets")}</h3>
        <ReportTable rows={data.assets} total={data.total_assets} totalLabel={t("rep.assets")} fmt={fmt} testid="sp-assets-table" />
      </div>
      <div>
        <h3 className="font-heading font-semibold mb-3 text-amber-500">{t("rep.liabilities")}</h3>
        <ReportTable rows={data.liabilities} total={data.total_liabilities} totalLabel={t("rep.liabilities")} fmt={fmt} testid="sp-liab-table" />
        <h3 className="font-heading font-semibold mt-6 mb-3 text-purple-500">{t("rep.equity")}</h3>
        <ReportTable rows={eqRows} total={data.total_equity + data.period_result} totalLabel={t("rep.equity")} fmt={fmt} testid="sp-equity-table" />
      </div>
    </div>
  );
}

function VatRegisters({ entity, fmt, t, lang }) {
  const [kind, setKind] = useState("sale");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    let url = `/reports/vat-register?entity_id=${entity}&kind=${kind}`;
    if (from) url += `&date_from=${from}`;
    if (to) url += `&date_to=${to}`;
    api.get(url).then((r) => setData(r.data)).catch(() => {});
  }, [entity, kind, from, to]);
  useEffect(() => { load(); }, [load]);

  const title = kind === "sale" ? t("rep.salesReg") : t("rep.purchReg");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 no-print">
        <div>
          <Label>{t("common.type")}</Label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-[220px]" data-testid="vat-register-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sale">{t("rep.salesReg")}</SelectItem>
              <SelectItem value="purchase">{t("rep.purchReg")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("rep.from")}</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="vat-register-from" />
        </div>
        <div>
          <Label>{t("rep.to")}</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="vat-register-to" />
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="printable-vat-register-btn">
          <Printer className="h-4 w-4 mr-2" />{t("common.print")}
        </Button>
      </div>

      <div className="print-area" data-testid="vat-register-print-area">
        <h2 className="font-heading font-bold text-lg mb-1">{title}</h2>
        <div className="text-xs text-muted-foreground mb-4">
          {entity.toUpperCase()} {from && `· ${t("rep.from")} ${from}`} {to && `· ${t("rep.to")} ${to}`}
        </div>
        {!data || data.rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">{t("common.noData")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-border text-left">
                <th className="py-2">{t("rep.protocol")}</th>
                <th className="py-2">{t("common.date")}</th>
                <th className="py-2">{t("common.number")}</th>
                <th className="py-2">{kind === "sale" ? t("inv.client") : t("inv.supplier")}</th>
                <th className="py-2 text-right">{t("common.net")}</th>
                <th className="py-2 text-right">{t("common.vat")}</th>
                <th className="py-2 text-right">{t("common.total")}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-b border-border">
                  <td className="py-2 font-mono">{r.protocol}</td>
                  <td className="py-2 font-mono">{r.date}</td>
                  <td className="py-2 font-mono">{r.number}</td>
                  <td className="py-2">{r.counterparty}</td>
                  <td className="py-2 text-right font-mono">{fmt(r.net, r.currency)}</td>
                  <td className="py-2 text-right font-mono">{fmt(r.vat, r.currency)}</td>
                  <td className="py-2 text-right font-mono">{fmt(r.total, r.currency)}</td>
                </tr>
              ))}
              <tr className="font-bold border-t-2 border-border">
                <td colSpan={4} className="py-2 uppercase text-xs tracking-wider">{t("common.total")}</td>
                <td className="py-2 text-right font-mono">{fmt(data.totals.net)}</td>
                <td className="py-2 text-right font-mono">{fmt(data.totals.vat)}</td>
                <td className="py-2 text-right font-mono">{fmt(data.totals.total)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function UkBalanceSheet({ entity, fmt, t }) {
  const [sp, setSp] = useState(null);
  const [ce, setCe] = useState(null);
  useEffect(() => {
    setSp(null); setCe(null);
    api.get(`/reports/stato-patrimoniale?entity_id=${entity}`).then((r) => setSp(r.data)).catch(() => {});
    api.get(`/reports/conto-economico?entity_id=${entity}`).then((r) => setCe(r.data)).catch(() => {});
  }, [entity]);
  if (!sp || !ce) return <div className="text-sm text-muted-foreground">{t("common.loading")}</div>;

  const gbp = (v) => fmt(v, "GBP");
  const rows = [
    { label: t("uk.turnover"), value: ce.total_revenues },
    { label: t("uk.admin"), value: -ce.total_expenses },
    { label: t("uk.profit"), value: ce.result, bold: true },
  ];
  const bsRows = [
    { label: t("uk.currentAssets"), value: sp.total_assets },
    { label: t("uk.creditors"), value: -sp.total_liabilities },
    { label: t("uk.netAssets"), value: sp.total_assets - sp.total_liabilities, bold: true },
    { label: t("uk.capital"), value: sp.total_equity },
    { label: t("uk.profit"), value: sp.period_result },
  ];
  const capitalTotal = sp.total_equity + sp.period_result;

  return (
    <div className="space-y-4">
      <div className="flex justify-end no-print">
        <Button variant="outline" onClick={() => window.print()} data-testid="print-uk-balance-btn">
          <Printer className="h-4 w-4 mr-2" />{t("common.print")}
        </Button>
      </div>
      <div className="print-area max-w-2xl" data-testid="uk-balance-sheet">
        <h2 className="font-heading font-bold text-xl mb-1">{t("uk.title")}</h2>
        <div className="text-xs text-muted-foreground mb-6">{entity.toUpperCase().replace("_", "-")} · GBP · {new Date().getFullYear()}</div>
        <table className="w-full text-sm mb-8">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className={`border-b border-border ${r.bold ? "font-bold" : ""}`}>
                <td className="py-2">{r.label}</td>
                <td className="py-2 text-right font-mono">{gbp(r.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table className="w-full text-sm">
          <tbody>
            {bsRows.map((r) => (
              <tr key={r.label} className={`border-b border-border ${r.bold ? "font-bold" : ""}`}>
                <td className="py-2">{r.label}</td>
                <td className="py-2 text-right font-mono">{gbp(r.value)}</td>
              </tr>
            ))}
            <tr className="font-bold border-t-2 border-border">
              <td className="py-2">{t("uk.capital")} — Total</td>
              <td className="py-2 text-right font-mono">{gbp(capitalTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExportTab({ entity, t }) {
  const download = async () => {
    try {
      const res = await api.get(`/reports/export?entity_id=${entity}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `export_${entity}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("msg.saved"));
    } catch {
      toast.error(t("msg.error"));
    }
  };
  return (
    <div className="max-w-lg space-y-4" data-testid="export-tab">
      <p className="text-sm text-muted-foreground">{t("rep.exportDesc")}</p>
      <Button onClick={download} data-testid="export-accountant-pack-btn">
        <DownloadCloud className="h-4 w-4 mr-2" />{t("common.export")} CSV
      </Button>
    </div>
  );
}

export default function Reports() {
  const { t, fmt, entity, lang } = useApp();
  return (
    <div className="space-y-6" data-testid="reports-page">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight no-print">{t("nav.reports")}</h1>
      <Card>
        <CardContent className="pt-6">
          <Tabs defaultValue="ce">
            <TabsList className="mb-6 flex-wrap no-print">
              <TabsTrigger value="ce" data-testid="tab-conto-economico">{t("rep.ce")}</TabsTrigger>
              <TabsTrigger value="sp" data-testid="tab-stato-patrimoniale">{t("rep.sp")}</TabsTrigger>
              <TabsTrigger value="vat" data-testid="tab-vat-registers">{t("rep.vat")}</TabsTrigger>
              <TabsTrigger value="uk" data-testid="tab-uk-balance">{t("rep.uk")}</TabsTrigger>
              <TabsTrigger value="export" data-testid="tab-export">{t("rep.export")}</TabsTrigger>
            </TabsList>
            <TabsContent value="ce" forceMount className="data-[state=inactive]:hidden">
              <div className="flex justify-end mb-4 no-print">
                <Button variant="outline" onClick={() => window.print()} data-testid="print-ce-btn">
                  <Printer className="h-4 w-4 mr-2" />{t("common.print")}
                </Button>
              </div>
              <ContoEconomico entity={entity} fmt={fmt} t={t} />
            </TabsContent>
            <TabsContent value="sp">
              <div className="flex justify-end mb-4 no-print">
                <Button variant="outline" onClick={() => window.print()} data-testid="print-sp-btn">
                  <Printer className="h-4 w-4 mr-2" />{t("common.print")}
                </Button>
              </div>
              <StatoPatrimoniale entity={entity} fmt={fmt} t={t} />
            </TabsContent>
            <TabsContent value="vat"><VatRegisters entity={entity} fmt={fmt} t={t} lang={lang} /></TabsContent>
            <TabsContent value="uk"><UkBalanceSheet entity={entity} fmt={fmt} t={t} /></TabsContent>
            <TabsContent value="export"><ExportTab entity={entity} t={t} /></TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
